import React, { useEffect, useMemo, useState } from 'react';
import PageWrapper from '../../components/PageWrapper';
import { Alert, Avatar, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, List, ListItemAvatar, ListItemButton, ListItemText, Menu, MenuItem, Snackbar, TextField, Typography } from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import { Link, useNavigate } from 'react-router-dom';
import { usePlanCreateMutation, usePlansListMutation } from '../../services/plans';
import { usePlanImportFromYTMutation, usePlanImportFromIGMutation, usePlanImportFromPinterestMutation } from '../../services/planImport';
import { useAppSelector } from '../../hooks';
import { selectUserProfile } from '../accountSlice';
import { useAuthEffects } from '../../components/Auth';
import { selectPlansOwned } from './planSlice';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';

export default function PlansListView() {
	useAuthEffects();

	const plans = useAppSelector(selectPlansOwned);
	const userProfile = useAppSelector(selectUserProfile);
	const [plansListMutation] = usePlansListMutation();
	const [isNewPlanDialogOpen, setIsNewPlanDialogOpen] = useState<boolean>(false);
	const [isYoutubeImportDialogOpen, setIsYoutubeImportDialogOpen] = useState<boolean>(false);
	const [isInstagramImportDialogOpen, setIsInstagramImportDialogOpen] = useState<boolean>(false);
	const [isPinterestImportDialogOpen, setIsPinterestImportDialogOpen] = useState<boolean>(false);
	const [planMenuAnchorEl, setPlanMenuAnchorEl] = useState<null | HTMLElement>(null);
	const [planImportFromYTMutation, {error: ytImportError, isSuccess: isYtImportSuccess}] = usePlanImportFromYTMutation();
	const [planImportFromIGMutation, {error: igImportError, isSuccess: isIgImportSuccess}] = usePlanImportFromIGMutation();
	const [planImportFromPinterestMutation, {error: pinterestImportError, isSuccess: isPinterestImportSuccess}] = usePlanImportFromPinterestMutation();
	const [planCreateMutation, {error: planCreateError, isSuccess: isPlanCreateSuccess}] = usePlanCreateMutation();
	const navigate = useNavigate();

	const importFromYoutubeHandleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const formData = new FormData(e.target as HTMLFormElement);
		planImportFromYTMutation({ videoId: formData.get('videoId') as string });
	};

	const importFromInstagramHandleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const formData = new FormData(e.target as HTMLFormElement);
		planImportFromIGMutation({
			videoId: formData.get('videoId') as string,
			title: formData.get('title') as string,
			description: formData.get('description') as string,
			channelId: formData.get('channelId') as string,
			channelBio: formData.get('channelBio') as string,
			thumbnailUrl: formData.get('thumbnailUrl') as string,
			authorThumbnailUrl: formData.get('authorThumbnailUrl') as string,
		});
	};

	const importFromPinterestHandleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const formData = new FormData(e.target as HTMLFormElement);
		planImportFromPinterestMutation({ 
			pinId: formData.get('pinId') as string,
			pinterestChannelId: formData.get('pinterestChannelId') as string
		});
	};

	const handleImportFromYoutubeClick = () => {
		setIsYoutubeImportDialogOpen(true);
		setPlanMenuAnchorEl(null);
	};

	const handleImportFromInstagramClick = () => {
		setIsInstagramImportDialogOpen(true);
		setPlanMenuAnchorEl(null);
	};

	const handleImportFromPinterestClick = () => {
		setIsPinterestImportDialogOpen(true);
		setPlanMenuAnchorEl(null);
	};

	const handleNewPlanFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const formData = new FormData(e.target as HTMLFormElement);
		planCreateMutation({ 
			title: formData.get('title') as string, 
			description: formData.get('description') as string
		}).then(({data: createdPlanData}) => {
			navigate(`/plans/${createdPlanData?.id}/edit`);
		});
	};

	const [isErrorState, setIsErrorState] = useState(false);
	const [isSuccessState, setIsSuccessState] = useState(false);
	const anyImportSuccess = useMemo(() => isYtImportSuccess || isIgImportSuccess || isPlanCreateSuccess || isPinterestImportSuccess, [isYtImportSuccess, isIgImportSuccess, isPlanCreateSuccess, isPinterestImportSuccess]);
	const anyImportError = useMemo(() => ytImportError || igImportError || planCreateError || pinterestImportError, [ytImportError, igImportError, planCreateError, pinterestImportError]);

	useEffect(() => {
		if (anyImportError) {
			setIsErrorState(true);
		}
	}, [anyImportError]);

	useEffect(() => {
		if (anyImportSuccess) {
			setIsYoutubeImportDialogOpen(false);
			setIsInstagramImportDialogOpen(false);
			setIsNewPlanDialogOpen(false);
			setIsPinterestImportDialogOpen(false);
		}
	}, [anyImportSuccess]);

	useEffect(() => {
		if (userProfile) {
			plansListMutation({});
		}
	}, [userProfile]);
	
	const handleCloseSnackbar = () => {
		setIsSuccessState(false);
		setIsErrorState(false);
	};

	const {t} = useTranslation();

	return <PageWrapper disableFooter={!plans.length} noScroll={!plans.length} maxWidth='md'>
		<Box flex={1} display='flex' flexDirection='column' gap={2} mt={2}>
			{/* {plans.length > 0 && } */}
			{!plans.length ? <Box flex={1} display='flex' alignItems='center' justifyContent='center' flexDirection='column' gap={3}>
				<Typography fontSize='1.2rem' textAlign='center'>No projects in your collection yet!</Typography>
				<Button variant='contained' size='large' onClick={() => setIsNewPlanDialogOpen(true)}>Add my first project</Button>
			</Box> :
				<>
					<Box display='flex' alignItems='flex-end' gap={1}>
						<Box flex={1}>
							<Typography variant='h6' mb={2}>{t('plans.myCollection')}</Typography>
							<Typography variant='body2'>{t('plans.diyProjectsByYou')}</Typography> 
						</Box>
						<div>
							<Button onClick={() => setIsNewPlanDialogOpen(true)}>{t('plans.createPlan')}</Button>
							<IconButton
								onClick={(event) => setPlanMenuAnchorEl(event.currentTarget)}
								aria-controls={planMenuAnchorEl ? 'plan-menu' : undefined}
								aria-haspopup="true"
								aria-expanded={planMenuAnchorEl ? 'true' : undefined}
							>
								<ExpandMoreIcon />
							</IconButton>
						</div>
						<Menu
							anchorEl={planMenuAnchorEl}
							open={Boolean(planMenuAnchorEl)}
							onClose={() => setPlanMenuAnchorEl(null)}
						>
							<MenuItem onClick={handleImportFromYoutubeClick}>Import from YouTube</MenuItem>
							<MenuItem onClick={handleImportFromInstagramClick}>Import from Instagram</MenuItem>
							<MenuItem onClick={handleImportFromPinterestClick}>Import from Pinterest</MenuItem>
						</Menu>
					</Box>
					<Divider />
					<List sx={{pb:5}}>
						{plans.map((plan, i) => <ListItemButton key={i} component={Link} to={`/plans/${plan.id}/edit`} sx={{cursor:'pointer'}}>
							<ListItemAvatar>
								<Avatar src={plan.imgUrl} variant='square' sx={{ width: 96, height: 96, mr: 2, borderRadius: 2 }}>
									<FolderIcon />
								</Avatar>
							</ListItemAvatar>
							<ListItemText>{plan.alternativeTitle || plan.title}</ListItemText>
							<Box>
								{plan.publishedAt ? <CheckCircleIcon color='success' /> : <UnpublishedIcon color='warning' /> }
							</Box>
						</ListItemButton>)}
					</List>
				</>}
		</Box>
		<Dialog open={isNewPlanDialogOpen} onClose={() => setIsNewPlanDialogOpen(false)} fullWidth maxWidth='xs'>
			<DialogTitle sx={{backgroundColor: 'divider'}}>Add Project</DialogTitle>
			<form onSubmit={handleNewPlanFormSubmit}>
				<DialogContent>
					<Box display='flex' flexDirection='column' gap={2}>
						<Typography variant='body1'>Showcase something you made and help DIY-ers around the world to follow your lead!</Typography>
						<TextField required name='title' label='Title' fullWidth />
						<TextField name='description' multiline rows={4} label='Description' fullWidth />
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setIsNewPlanDialogOpen(false)}>Cancel</Button>
					<Button disabled={isErrorState} type='submit' variant='contained'>Add</Button>
				</DialogActions>
			</form>
		</Dialog>
		<Dialog open={isYoutubeImportDialogOpen} onClose={() => setIsYoutubeImportDialogOpen(false)} fullWidth maxWidth='xs'>
			<DialogTitle sx={{backgroundColor: 'divider'}}>Import from YouTube</DialogTitle>
			<form onSubmit={importFromYoutubeHandleFormSubmit}>
				<DialogContent>
					<Box display='flex' flexDirection='column' gap={2}>
						<Typography variant='body1'>Have your content published on YouTube? Perfect! Connect it to your collection immediately!</Typography>
						<TextField required name='videoId' label='Youtube video id' fullWidth />
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setIsYoutubeImportDialogOpen(false)}>Cancel</Button>
					<Button disabled={isErrorState} type='submit' variant='contained'>Add</Button>
				</DialogActions>
			</form>
		</Dialog>
		<Dialog open={isInstagramImportDialogOpen} onClose={() => setIsInstagramImportDialogOpen(false)} fullWidth maxWidth='xs'>
			<DialogTitle>Import from Instagram</DialogTitle>
			<form onSubmit={importFromInstagramHandleFormSubmit}>
				<DialogContent sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
					<TextField required name='videoId' label='Instagram video id' fullWidth />
					<TextField required name='title' label='Title' fullWidth />
					<TextField required name='description' label='Description' fullWidth />
					<TextField required name='channelId' label='Channel ID' fullWidth />
					<TextField required name='channelBio' label='Channel Bio' fullWidth />
					<TextField name='thumbnailUrl' label='Thumbnail URL' fullWidth />
					<TextField name='channelThumbnailUrl' label='Channel thumbnail URL' fullWidth />
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setIsInstagramImportDialogOpen(false)}>Cancel</Button>
					<Button disabled={isErrorState} type='submit' variant='contained'>Add</Button>
				</DialogActions>
			</form>
		</Dialog>
		<Dialog open={isPinterestImportDialogOpen} onClose={() => setIsPinterestImportDialogOpen(false)} fullWidth maxWidth='xs'>
			<DialogTitle>Import from Pinterest</DialogTitle>
			<form onSubmit={importFromPinterestHandleFormSubmit}>
				<DialogContent sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
					<Typography variant='body1'>Have already published content on Pinterest? Perfect, connect it to your collection!</Typography>
					<TextField required name='pinId' label='Pinterest pin id' fullWidth />
					<TextField required name='pinterestChannelId' label='Pinterest channel id' fullWidth />
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setIsPinterestImportDialogOpen(false)}>Cancel</Button>
					<Button disabled={isErrorState} type='submit' variant='contained'>Add</Button>
				</DialogActions>
			</form>
		</Dialog>
		<Snackbar
			open={isSuccessState}
			anchorOrigin={{ vertical: 'bottom', horizontal: 'center'}}
			autoHideDuration={5000}
			onClose={handleCloseSnackbar}
		>
			<Alert
				severity="success"
				sx={{ width: '100%' }}
			>
				Project imported!
			</Alert>
		</Snackbar>
		<Snackbar
			open={isErrorState}
			anchorOrigin={{ vertical: 'bottom', horizontal: 'center'}}
			autoHideDuration={5000}
			onClose={handleCloseSnackbar}
		>
			<Alert
				severity="error"
				sx={{ width: '100%' }}
			>
				Could not import plan: {JSON.stringify((anyImportError as FetchBaseQueryError)?.data)}
			</Alert>
		</Snackbar>
	</PageWrapper>;
}
